import { UtensilsCrossed, Globe, Nfc, ScanLine, Camera, Clapperboard, ArrowUpRight } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";

const SERVICES = [
  {
    icon: UtensilsCrossed,
    title: "Menu Digitali",
    desc: "Menu moderni e interattivi: categorie, foto e video dei piatti, descrizioni, allergeni e prezzi. Un'esperienza digitale perfetta da smartphone — non un semplice PDF.",
    tags: ["Foto & Video", "Allergeni", "Multilingua"],
    span: "md:col-span-2",
    link: "/menu",
  },
  { icon: Globe, title: "Siti Web", desc: "Siti su misura per ristoranti, pizzerie, bar, hotel e attività commerciali. Design premium, responsive e velocissimi." },
  { icon: Nfc, title: "NFC & Recensioni", desc: "Card NFC che portano il cliente dove conta: Google Reviews, Instagram, menu digitale o sito web. Con un semplice tap." },
  { icon: ScanLine, title: "Piastrine NFC", desc: "Tag NFC eleganti e discreti per tavoli e banconi. Lo smartphone si avvicina e il menu si apre all'istante." },
  { icon: Camera, title: "Fotografia Professionale", desc: "Shooting editoriali e cinematografici: piatti, ambienti, cocktail, prodotti e staff." },
  { icon: Clapperboard, title: "Video & Drone", desc: "Video professionali e riprese aeree per locali, strutture, eventi e panorami.", span: "md:col-span-2" },
];

const Services = () => (
  <section id="servizi" data-testid="services-section" className="marble-bg-soft relative py-28 lg:py-40">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
      <div className="mb-20 grid gap-10 lg:grid-cols-2 lg:items-end">
        <Reveal>
          <Overline className="mb-6">I nostri servizi</Overline>
          <h2 className="font-display text-4xl font-medium leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
            Tutto ciò che serve<br />per <span className="gold-text italic">distinguersi.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="max-w-md text-base font-light leading-relaxed text-[#A3A3A3] lg:ml-auto">
            Dalla presenza digitale all'esperienza del cliente, costruiamo strumenti progettati per rendere il tuo locale più moderno, riconoscibile e professionale.
          </p>
        </Reveal>
      </div>

      <div className="grid gap-px bg-[#C19D60]/10 md:grid-cols-3">
        {SERVICES.map((s, i) => {
          const cardClass = "card-surface group relative flex h-full flex-col justify-between overflow-hidden p-9 transition-colors duration-700 hover:bg-[#151517]";
          const testId = `service-card-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
          const inner = (
            <>
              <div className="pointer-events-none absolute inset-x-0 top-0 h-px origin-left scale-x-0 bg-gradient-to-r from-[#C19D60] to-transparent transition-transform duration-700 group-hover:scale-x-100" />
              <div>
                <div className="mb-8 flex items-start justify-between">
                  <s.icon size={30} strokeWidth={1} className="text-[#C19D60] transition-transform duration-700 group-hover:-translate-y-1" />
                  <span className="font-display text-sm italic text-[#C19D60]/40">0{i + 1}</span>
                </div>
                <h3 className="font-display text-2xl font-medium tracking-tight text-[#F8F5F0]">{s.title}</h3>
                <p className="mt-4 text-sm font-light leading-relaxed text-[#A3A3A3]">{s.desc}</p>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-2">
                {s.tags?.map((t) => (
                  <span key={t} className="border border-[#C19D60]/25 px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-[#E5CAA4]/80">
                    {t}
                  </span>
                ))}
                {s.link && (
                  <span className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.18em] text-[#C19D60] transition-colors duration-500 group-hover:text-[#E5CAA4]">
                    Vedi il menù <ArrowUpRight size={12} className="transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                )}
              </div>
            </>
          );
          return (
            <Reveal key={s.title} delay={0.06 * i} className={s.span || ""}>
              {s.link ? (
                <a href={s.link} data-testid={testId} className={cardClass}>
                  {inner}
                </a>
              ) : (
                <article data-testid={testId} className={cardClass}>
                  {inner}
                </article>
              )}
            </Reveal>
          );
        })}

        <Reveal delay={0.4}>
          <a
            href="#contatti"
            data-testid="services-cta-card"
            className="group relative flex h-full min-h-[220px] flex-col justify-between overflow-hidden bg-[#C19D60] p-9 text-[#050505]"
          >
            <ArrowUpRight size={30} strokeWidth={1.2} className="transition-transform duration-700 group-hover:translate-x-1 group-hover:-translate-y-1" />
            <div>
              <h3 className="font-display text-2xl font-medium">Non sai da dove partire?</h3>
              <p className="mt-3 text-sm font-medium">Parliamone. Studiamo insieme la soluzione giusta per il tuo locale.</p>
            </div>
          </a>
        </Reveal>
      </div>
    </div>
  </section>
);

export default Services;
